import { Link, useLocation } from 'react-router-dom';
import { useKeyboardOpen } from '@/hooks/useKeyboardOpen';

type TabItem = { path: string; label: string };

// Reports is desktop-first work (review, share, edit) — inspectors don't do
// that on a phone in the field. Dropped from the mobile bottom nav per #69;
// report actions live inside the inspection detail view after publish.
// /reports route stays accessible via direct URL + the desktop sidebar.
const ITEMS: TabItem[] = [
  { path: '/inspections', label: 'Inspections' },
  { path: '/settings', label: 'Settings' },
];

function TabIcon({ name, active }: { name: string; active: boolean }) {
  const cls = active ? 'text-[color:var(--brand-600)]' : 'text-[color:var(--fg-4)]';
  // 28px icons (was 24) so the tab content has more visual presence relative
  // to the safe-area-inset-bottom dead space below the labels.
  switch (name) {
    case '/inspections':
      return (
        <svg className={`w-7 h-7 ${cls}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      );
    default:
      return (
        <svg className={`w-7 h-7 ${cls}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
  }
}

export function MobileTabBar() {
  const location = useLocation();
  const keyboardOpen = useKeyboardOpen();
  return (
    <nav
      // Slide off-screen while the keyboard is open: between the keyboard,
      // Safari's URL bar, and the QuickActionBar, the bottom nav was eating
      // ~50% of the visible viewport while the inspector was typing.
      className={
        'md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t transition-transform duration-200 ' +
        (keyboardOpen ? 'translate-y-full pointer-events-none' : '')
      }
      style={{ borderColor: 'var(--border-1)' }}
      aria-label="Mobile primary"
      aria-hidden={keyboardOpen}
    >
      <ul className="flex" style={{ paddingBottom: 'env(safe-area-inset-bottom,0px)' }}>
        {ITEMS.map((item) => {
          const active = location.pathname.startsWith(item.path);
          return (
            <li key={item.path} className="flex-1">
              <Link
                to={item.path}
                // 64px (was 56), gap-1 (was 0.5), text-xs semibold (was
                // text-[10px] medium) — more visual weight so the dead
                // space below the labels (safe-area-inset for the home
                // indicator, which we keep for accessibility) becomes a
                // smaller proportion of the visible bar.
                className="flex flex-col items-center gap-1 py-2 px-1 min-h-[64px] justify-center"
              >
                <TabIcon name={item.path} active={active} />
                <span
                  className="text-xs font-semibold"
                  style={{ color: active ? 'var(--brand-600)' : 'var(--fg-4)' }}
                >
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
